import { useMemo, useState } from 'react';
import { ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { useLocalization } from '@progress/kendo-react-intl';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useIdentityStore from '../../../../Zustand/identityStore';
import { enMessages } from '../../../../messages/en-US';
import { ReactQueryKeys } from '../../../../Constants/ClientRoutingConstants';
import machinesApiService from '../../../Assets/Utilities/MachinesApiService';
import type { EntityNoteCategory } from '../Components/Notes/EntityNotesPanel';
import notesApiService from '../Utilities/NotesApiService';
import type { Note } from '../Components/Notes/EntityNotesPanel';
import { useNoteCategories } from '../Components/Notes/noteHooks';
import { getEntityNoteIcon } from '../Components/Notes/entityNoteIcon';
import NotesTimelineView, { TimelineViewNote, defaultNotesDateRange } from '../Components/Notes/NotesTimelineView';
import { ENTITY_TYPE_OPTIONS } from '../Components/Notes/entityTypeOptions';

// Dashboard-wide notes span every asset. `Device` covers gateways (the backend dropped `Gateway`).
type DashEntityType = 'Asset' | 'Endpoint' | 'Device' | 'GreasePump';

export default function DashboardNotesTab() {
  const localizationService = useLocalization();
  const currentUserId = useIdentityStore((x) => x.user?.profile?.sub) || '';
  const queryClient = useQueryClient();
  const { categoryLabel } = useNoteCategories();

  // Date range owns the server query (refetches on change); the view renders the pickers.
  const [dateRange, setDateRange] = useState(defaultNotesDateRange);
  const startISO = dateRange.start ? dateRange.start.toISOString() : undefined;
  const endISO = dateRange.end ? dateRange.end.toISOString() : undefined;
  const notesQuery = useQuery(
    ['siteAllNotes', startISO, endISO],
    () => notesApiService.getAllSiteNotes(startISO, endISO),
    { staleTime: 30 * 1000, keepPreviousData: true, retry: false },
  );

  const allNotes = useMemo<Note[]>(
    () =>
      (notesQuery.data ?? [])
        .slice()
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    [notesQuery.data],
  );

  // Asset filter options — every site asset (not just those with notes). Notes only carry
  // `assetName` (no assetId), so the filter matches by name; distinct names, sorted.
  const machinesQuery = useQuery(ReactQueryKeys.AllMachinesQuery, () => machinesApiService.getAllMachines(), {
    staleTime: 5 * 60 * 1000,
  });
  const assetOptions = useMemo(
    () =>
      Array.from(new Set((machinesQuery.data ?? []).map((m: { name: string }) => m.name).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [machinesQuery.data],
  );

  const invalidateNotes = () => queryClient.invalidateQueries(['siteAllNotes']);
  const editNoteMutation = useMutation(
    ({ noteId, input }: { noteId: string; input: { description: string; categories: EntityNoteCategory[]; isInternalOnly?: boolean } }) =>
      notesApiService.updateNote(noteId, { comment: input.description, categories: input.categories, isInternalOnly: input.isInternalOnly }),
    { onSuccess: invalidateNotes },
  );
  const deleteNoteMutation = useMutation(
    (noteId: string) => notesApiService.deleteNote(noteId),
    { onSuccess: invalidateNotes },
  );

  const labelFor = (entityType: string) => ENTITY_TYPE_OPTIONS.find((t) => t.value === entityType)?.text ?? entityType;

  /** Identity header: entity icon + asset name, with a "Name · Serial" subtitle for child entities. */
  const renderNoteHeader = (n: Note, typeLabel: string) => {
    const isAsset = n.entityType === 'Asset';
    const subParts = isAsset ? [] : [n.entityName, n.entityMetadata.serialNo].filter(Boolean);
    return (
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="inline-flex shrink-0 items-center justify-center" title={typeLabel}>
          {getEntityNoteIcon(n.entityType, n.entityMetadata.deviceType, 18, n.entityMetadata.criticality)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-snug" title={n.assetName}>
            {n.assetName}
          </div>
          {subParts.length > 0 && (
            <div className="truncate text-xs text-gray-500" title={subParts.join(' · ')}>
              {subParts.join(' · ')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const viewNotes = useMemo<TimelineViewNote[]>(
    () =>
      allNotes.map((n) => {
        // The site-wide endpoint may return `Point` notes; render them as Endpoints.
        const entityType: DashEntityType = n.entityType === 'Point' ? 'Endpoint' : (n.entityType as DashEntityType);
        const typeLabel = labelFor(entityType);
        const categoryLabels = n.categories.map(categoryLabel).join(' ');
        const assetName = n.assetName ?? '';
        const entityName = entityType === 'Asset' ? '' : n.entityName ?? '';
        const serialNumber = n.entityMetadata.serialNo ?? '';
        return {
          ...n,
          entityType,
          header: renderNoteHeader(n, typeLabel),
          searchText: `${n.description} ${n.createdBy} ${assetName} ${entityName} ${serialNumber} ${typeLabel} ${categoryLabels}`.toLowerCase(),
          exportRow: {
            createdAt: moment(n.createdAt).format('DD/MM/YYYY HH:mm'),
            entityTypeLabel: typeLabel,
            assetName,
            entityName,
            serialNumber,
            categories: n.categories.map(categoryLabel).join(', '),
            description: n.description,
            createdBy: n.createdBy,
          },
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allNotes],
  );

  const exportColumns = (
    <>
      <ExcelExportColumn field="createdAt" title={localizationService.toLanguageString('custom.date', enMessages.custom.date)} width={140} />
      <ExcelExportColumn field="entityTypeLabel" title="Entity Type" width={120} />
      <ExcelExportColumn field="assetName" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} width={200} />
      <ExcelExportColumn field="entityName" title="Entity" width={180} />
      <ExcelExportColumn field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} width={150} />
      <ExcelExportColumn field="categories" title={localizationService.toLanguageString('custom.category', enMessages.custom.category)} width={200} />
      <ExcelExportColumn field="description" title={localizationService.toLanguageString('custom.description', enMessages.custom.description)} width={400} />
      <ExcelExportColumn field="createdBy" title={localizationService.toLanguageString('custom.createdBy', enMessages.custom.createdBy)} width={160} />
    </>
  );

  return (
    <NotesTimelineView
      notes={viewNotes}
      entityTypeOptions={ENTITY_TYPE_OPTIONS}
      assetOptions={assetOptions}
      exportColumns={exportColumns}
      exportFileName="Notes.xlsx"
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      isLoading={notesQuery.isLoading}
      isError={notesQuery.isError}
      errorMessage={(notesQuery.error as Error)?.message}
      isFetching={notesQuery.isFetching}
      onRefresh={() => notesQuery.refetch()}
      onSave={(noteId, input) => editNoteMutation.mutate({ noteId, input })}
      onDelete={(noteId) => deleteNoteMutation.mutate(noteId)}
      currentUserId={currentUserId}
    />
  );
}
